import { Fragment, PureComponent, ReactNode } from 'react';
import { getMCertTemplates } from '../../api/mcertTemplate';
import AppLoader from './AppLoader';
import { CERTIFICATES, CERTIFICATES_CONFIG, CUSTOM_CERT_ID } from './medicalCertificates';
import { captureError } from '../../pages/captureError';
import { notification } from 'antd';
import { track } from '../../tracking';
import MakeYourOwnCert from './MakeYourOwnCert';
import { getMySingleDoctorId } from '../../store/idbHandler';

type Props = {
    onContinue: (id: string, type: 'mcert' | 'consent') => void;
};

type State = {
    selectedCertificate?: string;
    isLoading: boolean;
};

class SelectCr extends PureComponent<Props, State> {
    state: State = {
        selectedCertificate: undefined,
        isLoading: true,
    };

    certs = Array.from(CERTIFICATES);

    componentDidMount = async (): Promise<void> => {
        // fetch m cert from api
        getMCertTemplates(getMySingleDoctorId() || '')
            .then((res) => {
                if (res.success) {
                    res.templates.forEach((t) => {
                        this.certs.push({
                            id: t.id,
                            initial: t.name ? t.name[0] : 'C',
                            color: 'bg-darwin-accent-diagnosis-purple-800',
                            type: t.type,
                        });
                        CERTIFICATES_CONFIG[t.id] = {
                            heading: decodeURI(t.heading),
                            template: decodeURI(t.template),
                            name: t.name,
                        };
                    });

                    this.setState({ isLoading: false });
                }
            })
            .catch((err) => {
                captureError(err);
                console.error(err);
                this.setState({ isLoading: false });
                notification.error({
                    message: 'Failed to fetch certificate templates',
                    description: 'Please try again later',
                    duration: 5,
                });
                track('ERROR', { on: 'getMCertTemplates', what: err.message });
            });
    };

    setSelectedCertificate = (id: string, type: 'mcert' | 'consent'): void => {
        track('SELECT_CERTIFICATE', { id, type });
        const { onContinue } = this.props;
        this.setState({ selectedCertificate: id });
        onContinue(id, type);
    };

    render(): ReactNode {
        const { selectedCertificate, isLoading } = this.state;

        if (isLoading) {
            return (
                <div className="flex flex-1 flex-auto w-full h-full items-center justify-center">
                    <AppLoader />
                </div>
            );
        }
        return (
            <Fragment>
                <div className="p-16 flex-1 overflow-auto">
                    <div className="flex">
                        <div className="w-50p">
                            <p className="font-700">Select a medical certificate template</p>
                            <div className="grid grid-cols-mCert py-16 gap-x-11 gap-y-15 w-full">
                                <MakeYourOwnCert
                                    selectedCertificate={selectedCertificate}
                                    onContinue={() =>
                                        this.setSelectedCertificate(CUSTOM_CERT_ID, 'mcert')
                                    }
                                />
                                {this.certs
                                    .filter((t) => t.type !== 'consent')
                                    .map((c) => (
                                        <div
                                            key={c.id}
                                            className={`border border-darwin-neutral-100 p-8 rounded-8 text-center cursor-pointer min-h-108 ${
                                                selectedCertificate === c.id
                                                    ? 'bg-darwin-primary-100'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                this.setSelectedCertificate(c.id, 'mcert')
                                            }
                                        >
                                            <p
                                                className={`font-700 text-white  w-32 h-32 mt-6 mb-5 m-auto flex items-center justify-center rounded-full ${c.color}`}
                                            >
                                                {c.initial}
                                            </p>
                                            <p className="text-darwin-neutral-900 break-words">
                                                {CERTIFICATES_CONFIG[c.id]?.name}
                                            </p>
                                        </div>
                                    ))}
                            </div>
                        </div>

                        <div className="w-50p">
                            <p className="font-700">Select a consent form template</p>
                            <div className="grid grid-cols-mCert py-16 gap-x-11 gap-y-15 w-full">
                                <MakeYourOwnCert
                                    selectedCertificate={selectedCertificate}
                                    onContinue={() =>
                                        this.setSelectedCertificate(CUSTOM_CERT_ID, 'consent')
                                    }
                                />
                                {this.certs
                                    .filter((t) => t.type === 'consent')
                                    .map((c) => (
                                        <div
                                            key={c.id}
                                            className={`border border-darwin-neutral-100 p-8 rounded-8 text-center cursor-pointer min-h-108 ${
                                                selectedCertificate === c.id
                                                    ? 'bg-darwin-primary-100'
                                                    : ''
                                            }`}
                                            onClick={() =>
                                                this.setSelectedCertificate(c.id, 'consent')
                                            }
                                        >
                                            <p
                                                className={`font-700 text-white  w-32 h-32 mt-6 mb-5 m-auto flex items-center justify-center rounded-full ${c.color}`}
                                            >
                                                {c.initial}
                                            </p>
                                            <p className="text-darwin-neutral-900 break-words">
                                                {CERTIFICATES_CONFIG[c.id]?.name}
                                            </p>
                                        </div>
                                    ))}
                            </div>
                        </div>
                    </div>
                </div>
            </Fragment>
        );
    }
}

export default SelectCr;
